import { trackEvent } from "../posthog";

// Authentication Events
export const trackLogin = (email: string, method: string = "email") => {
  trackEvent("user_login", {
    email,
    method,
    timestamp: new Date().toISOString(),
  });
};

export const trackLogout = () => {
  trackEvent("user_logout", {
    timestamp: new Date().toISOString(),
  });
};

export const trackSignup = (email: string, companyName?: string) => {
  trackEvent("user_signup", {
    email,
    company_name: companyName,
    timestamp: new Date().toISOString(),
  });
};

export const trackOTPSent = (email: string) => {
  trackEvent("otp_sent", {
    email,
    timestamp: new Date().toISOString(),
  });
};

export const trackOTPVerified = (email: string) => {
  trackEvent("otp_verified", {
    email,
    timestamp: new Date().toISOString(),
  });
};

// Role Management Events
export const trackRoleCreated = (
  roleId: string,
  roleName: string,
  slug: string
) => {
  trackEvent("role_created", {
    role_id: roleId,
    role_name: roleName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackRoleUpdated = (
  roleId: string,
  roleName: string,
  slug: string
) => {
  trackEvent("role_updated", {
    role_id: roleId,
    role_name: roleName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackRoleDeleted = (
  roleId: string,
  roleName: string,
  slug: string
) => {
  trackEvent("role_deleted", {
    role_id: roleId,
    role_name: roleName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackRoleViewed = (
  roleId: string,
  roleName: string,
  slug: string
) => {
  trackEvent("role_viewed", {
    role_id: roleId,
    role_name: roleName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackRolePublished = (
  roleId: string,
  roleName: string,
  slug: string
) => {
  trackEvent("role_published", {
    role_id: roleId,
    role_name: roleName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// Candidate Events
export const trackCandidateInvited = (
  candidateEmail: string,
  roleId: string,
  roleName: string,
  slug: string
) => {
  trackEvent("candidate_invited", {
    candidate_email: candidateEmail,
    role_id: roleId,
    role_name: roleName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackCandidateViewed = (
  candidateId: string,
  candidateName: string,
  roleId: string,
  slug: string
) => {
  trackEvent("candidate_viewed", {
    candidate_id: candidateId,
    candidate_name: candidateName,
    role_id: roleId,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackCandidateShortlisted = (
  candidateId: string,
  candidateName: string,
  roleId: string,
  slug: string
) => {
  trackEvent("candidate_shortlisted", {
    candidate_id: candidateId,
    candidate_name: candidateName,
    role_id: roleId,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackCandidateRejected = (
  candidateId: string,
  candidateName: string,
  roleId: string,
  slug: string
) => {
  trackEvent("candidate_rejected", {
    candidate_id: candidateId,
    candidate_name: candidateName,
    role_id: roleId,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackBulkCandidateInvite = (
  count: number,
  roleId: string,
  roleName: string,
  slug: string
) => {
  trackEvent("bulk_candidate_invite", {
    count,
    role_id: roleId,
    role_name: roleName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// Report Events
export const trackReportViewed = (
  candidateId: string,
  candidateName: string,
  reportType: string,
  slug: string
) => {
  trackEvent("report_viewed", {
    candidate_id: candidateId,
    candidate_name: candidateName,
    report_type: reportType,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackReportDownloaded = (
  candidateId: string,
  candidateName: string,
  reportType: string,
  slug: string
) => {
  trackEvent("report_downloaded", {
    candidate_id: candidateId,
    candidate_name: candidateName,
    report_type: reportType,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// Integration Events
export const trackCalendlyIntegration = (roleId: string, slug: string) => {
  trackEvent("calendly_integration_used", {
    role_id: roleId,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackWhatsappMessageSent = (
  candidateId: string,
  messageType: string,
  slug: string
) => {
  trackEvent("whatsapp_message_sent", {
    candidate_id: candidateId,
    message_type: messageType,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// File Upload Events
export const trackFileUploaded = (
  fileType: string,
  fileName: string,
  fileSize: number,
  context: string,
  slug: string
) => {
  trackEvent("file_uploaded", {
    file_type: fileType,
    file_name: fileName,
    file_size: fileSize,
    context,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// Search and Filter Events
export const trackSearch = (
  searchTerm: string,
  context: string,
  slug: string
) => {
  trackEvent("search_performed", {
    search_term: searchTerm,
    context,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackFilterApplied = (
  filterType: string,
  filterValue: string,
  context: string,
  slug: string
) => {
  trackEvent("filter_applied", {
    filter_type: filterType,
    filter_value: filterValue,
    context,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// Error Events
export const trackError = (
  errorType: string,
  errorMessage: string,
  context: string,
  slug?: string
) => {
  trackEvent("error_occurred", {
    error_type: errorType,
    error_message: errorMessage,
    context,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// User Engagement Events
export const trackFeatureUsed = (featureName: string, slug: string) => {
  trackEvent("feature_used", {
    feature_name: featureName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackButtonClick = (
  buttonName: string,
  context: string,
  slug?: string
) => {
  trackEvent("button_clicked", {
    button_name: buttonName,
    context,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackModalOpened = (modalName: string, slug?: string) => {
  trackEvent("modal_opened", {
    modal_name: modalName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackModalClosed = (modalName: string, slug?: string) => {
  trackEvent("modal_closed", {
    modal_name: modalName,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// Trial and Subscription Events
export const trackTrialStarted = (email: string, companyName: string) => {
  trackEvent("trial_started", {
    email,
    company_name: companyName,
    timestamp: new Date().toISOString(),
  });
};

export const trackUpgradeClicked = (currentPlan: string, slug: string) => {
  trackEvent("upgrade_clicked", {
    current_plan: currentPlan,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// Job Widget Events
export const trackJobWidgetViewed = (
  widgetType: string,
  roleId: string,
  slug: string
) => {
  trackEvent("job_widget_viewed", {
    widget_type: widgetType,
    role_id: roleId,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackJobWidgetConfigured = (
  widgetType: string,
  roleId: string,
  slug: string
) => {
  trackEvent("job_widget_configured", {
    widget_type: widgetType,
    role_id: roleId,
    slug,
    timestamp: new Date().toISOString(),
  });
};

// AI/Interview Designer Events
export const trackInterviewDesignerUsed = (
  action: string,
  roleId?: string,
  slug?: string
) => {
  trackEvent("interview_designer_used", {
    action,
    role_id: roleId,
    slug,
    timestamp: new Date().toISOString(),
  });
};

export const trackAIQuestionGenerated = (
  questionType: string,
  roleId: string,
  slug: string
) => {
  trackEvent("ai_question_generated", {
    question_type: questionType,
    role_id: roleId,
    slug,
    timestamp: new Date().toISOString(),
  });
};
